import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PayPalButtons } from '@paypal/react-paypal-js';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiArrowLeft, FiShield, FiDollarSign, FiRefreshCw } = FiIcons;

const PaymentGate = ({ onPaymentSuccess, onBack, showToast }) => {
  const [processing, setProcessing] = useState(false);

  const createSubscription = (data, actions) => {
    return actions.subscription.create({
      plan_id: 'P-2WB272785G905180VNFCKZ4A'
    });
  };

  const onApprove = async (data, actions) => {
    setProcessing(true);
    try {
      const subscriptionID = data.subscriptionID;
      
      console.log('Subscription approved:', {
        subscriptionID,
        orderID: data.orderID,
        facilitatorAccessToken: data.facilitatorAccessToken
      });
      
      // Calculate next billing date (30 days from now)
      const startDate = new Date();
      const nextBillingDate = new Date(startDate);
      nextBillingDate.setDate(nextBillingDate.getDate() + 30);

      // Record payment in database with comprehensive details
      const { data: paymentData, error } = await supabase
        .from('payments')
        .insert({
          paypal_order_id: subscriptionID,
          subscription_id: subscriptionID,
          amount: 9.97,
          payment_type: 'subscription',
          status: 'active',
          next_billing_date: nextBillingDate.toISOString(),
          subscription_start_date: startDate.toISOString(),
          billing_cycle: 'monthly',
          plan_id: 'P-2WB272785G905180VNFCKZ4A'
        })
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      showToast('Subscription activated! Setting up your account...', 'success');
      
      // Pass comprehensive subscription data to registration
      onPaymentSuccess({
        subscriptionId: subscriptionID,
        paymentId: paymentData.id,
        nextBillingDate: nextBillingDate.toISOString(),
        startDate: startDate.toISOString(),
        amount: 9.97,
        status: 'active'
      });
    } catch (error) {
      console.error('Subscription error:', error);
      showToast('Subscription processing failed. Please try again.', 'error');
      setProcessing(false);
    }
  };

  const onError = (err) => {
    console.error('PayPal error:', err);
    showToast('Subscription failed. Please try again.', 'error');
    setProcessing(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -30 }}
        transition={{ duration: 0.6 }}
        className="max-w-lg w-full"
      >
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8">
          <div className="text-center mb-8">
            <div className="inline-block bg-blue-600/20 p-4 rounded-2xl mb-4">
              <SafeIcon icon={FiShield} className="w-12 h-12 text-blue-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Start Your Subscription</h2>
            <p className="text-slate-400">$9.97 monthly - automatic recurring billing</p>
          </div>

          <div className="bg-slate-800/70 rounded-2xl p-6 mb-8 border border-slate-700/50">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-300 font-medium">Sneaky Redirects</span>
              <span className="text-slate-400 text-sm flex items-center space-x-1">
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                <span>Monthly</span>
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-400" />
                <span className="text-4xl font-bold text-white">9.97</span>
                <span className="text-slate-400 text-lg">/month</span>
              </div>
              <span className="text-slate-400">USD</span>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-700/50">
              <p className="text-slate-400 text-sm">✓ Unlimited redirects</p>
              <p className="text-slate-400 text-sm">✓ Save and manage redirects</p>
              <p className="text-slate-400 text-sm">✓ Advanced encryption</p>
              <p className="text-slate-400 text-sm">✓ 24/7 access</p>
              <p className="text-slate-400 text-sm">✓ Cancel anytime</p>
            </div>
          </div>

          <div className="mb-6">
            {processing ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                <p className="text-slate-400 mt-4">Processing subscription...</p>
              </div>
            ) : (
              <PayPalButtons
                style={{ 
                  layout: 'vertical',
                  color: 'gold',
                  shape: 'rect',
                  label: 'subscribe'
                }}
                createSubscription={createSubscription}
                onApprove={onApprove}
                onError={onError}
                disabled={processing}
              />
            )}
          </div>

          <div className="text-center text-sm text-slate-500">
            <p>🔒 Secure payment powered by PayPal</p>
            <p className="mt-2">Automatic billing every month</p>
            <p className="mt-1">Cancel anytime from your PayPal account</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentGate;